/**
 * Error reporting for the browser (Sentry), gated on the server's word.
 *
 * The bundle never knows a DSN at build time. GET /api/health carries
 * `sentry_dsn` — LOREMAP_WEB_SENTRY_DSN on the server, null when unset — and
 * only then is `@sentry/react` loaded, as a dynamic import in its own chunk:
 * a local or unconfigured deploy runs the same bytes as before and fetches
 * nothing extra. Until a DSN arrives (or without one, forever) every function
 * here is a no-op, and none of them throws — a broken reporter must never be
 * a second bug on top of the one being reported.
 *
 * What is sent: uncaught errors and unhandled promise rejections (the SDK
 * hooks window.onerror / unhandledrejection itself on init) and what the
 * error boundaries hand to `reportError` (React swallows a render error
 * before the window ever hears of it). What is not: PII the SDK could gather
 * on its own (`sendDefaultPii: false`), performance traces, cookies or auth
 * headers, and any string shaped like an Anthropic key, a bearer token or a
 * PostgreSQL URL — `scrubEvent` masks those in every field before the event
 * leaves the tab. Same shapes and vocabulary as the server's observability.py.
 */

import type { ErrorEvent as SentryErrorEvent } from '@sentry/react';

type SentryModule = typeof import('@sentry/react');

export interface ErrorReportingOptions {
  /** One shared deploy vs. someone's laptop; the server's `hosted` flag decides. */
  environment?: 'local' | 'hosted';
  /** The build this tab runs — the server's version, since the SPA ships inside it. */
  release?: string;
}

/** The loaded SDK once `init` has run; null before, and forever without a DSN. */
let sentry: SentryModule | null = null;
/** The one load in flight or finished — a second call joins it instead of starting another. */
let starting: Promise<void> | null = null;

const SECRET_PATTERNS: ReadonlyArray<readonly [RegExp, string]> = [
  [/sk-ant-[A-Za-z0-9_-]+/g, '[REDACTED]'],
  [/\bbearer\s+[A-Za-z0-9._~+/=-]+/gi, 'Bearer [REDACTED]'],
  [/postgres(?:ql)?:\/\/[^\s'"]+/gi, 'postgresql://[REDACTED]'],
];

const DROPPED_HEADERS = new Set(['cookie', 'authorization']);

export function scrubText(text: string): string {
  let out = text;
  for (const [pattern, replacement] of SECRET_PATTERNS) out = out.replace(pattern, replacement);
  return out;
}

/** Every string in the event, wherever it sits: the tree is plain data by the time beforeSend sees it. */
function scrubValue(value: unknown): unknown {
  if (typeof value === 'string') return scrubText(value);
  if (Array.isArray(value)) return value.map(scrubValue);
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value)) out[key] = scrubValue(item);
    return out;
  }
  return value;
}

/** The `beforeSend` body: cookies and auth headers gone, secrets masked. */
export function scrubEvent(event: SentryErrorEvent): SentryErrorEvent {
  const request = event.request;
  if (request !== undefined) {
    delete request.cookies;
    if (request.headers !== undefined) {
      for (const name of Object.keys(request.headers)) {
        if (DROPPED_HEADERS.has(name.toLowerCase())) delete request.headers[name];
      }
    }
  }
  return scrubValue(event) as SentryErrorEvent;
}

function environmentFromHostname(): 'local' | 'hosted' {
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1' || host === '[::1]' ? 'local' : 'hosted';
}

/**
 * Turn reporting on for this tab, once. Null or empty: nothing happens and
 * nothing is loaded. Resolves when the SDK is ready (or when it was not to
 * be); callers may ignore it.
 */
export function startErrorReporting(
  dsn: string | null | undefined,
  options: ErrorReportingOptions = {},
): Promise<void> {
  if (starting !== null) return starting;
  if (!dsn) return Promise.resolve();
  starting = import('@sentry/react')
    .then((mod) => {
      mod.init({
        dsn,
        sendDefaultPii: false,
        tracesSampleRate: 0,
        environment: options.environment ?? environmentFromHostname(),
        release: options.release,
        beforeSend: scrubEvent,
      });
      sentry = mod;
    })
    .catch(() => {
      // The chunk did not load (offline; a blocker keyed on the package name):
      // the app is unaffected, it just reports nothing this session.
    });
  return starting;
}

/**
 * Hand one caught error to Sentry — what the boundaries call, since React
 * swallows a render error before window.onerror hears of it. A no-op until
 * `startErrorReporting` has finished, and never throws.
 */
export function reportError(error: unknown, context?: Record<string, unknown>): void {
  if (sentry === null) return;
  try {
    sentry.captureException(error, context === undefined ? undefined : { extra: context });
  } catch {
    // A failing reporter is not the reader's problem.
  }
}
